import { v4 as uuidv4 } from "uuid";
import type {
  LogLevel,
  LoggerConfig,
  LoggerContext,
  LogEntry,
  ILogger,
} from "./types";

export class Logger implements ILogger {
  public config: LoggerConfig;
  private correlationId: string | null = null;
  private context: LoggerContext = {};

  constructor(config: LoggerConfig) {
    this.config = config;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    };
    return levels[level] >= levels[this.config.level];
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    additionalContext?: LoggerContext,
  ): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      correlationId: this.correlationId || undefined,
      ...this.context,
      ...additionalContext,
    };

    return entry;
  }

  private logToConsole(entry: LogEntry): void {
    if (!this.config.enableConsole) return;

    const { level, message, timestamp, ...context } = entry;
    const logMessage = `[${timestamp}] ${message}`;

    switch (level) {
      case "debug":
        console.debug(logMessage, context);
        break;
      case "info":
        console.info(logMessage, context);
        break;
      case "warn":
        console.warn(logMessage, context);
        break;
      case "error":
        console.error(logMessage, context);
        break;
    }
  }

  debug(message: string, context?: LoggerContext): void {
    if (!this.shouldLog("debug")) return;

    const entry = this.createLogEntry("debug", message, context);
    this.logToConsole(entry);
  }

  info(message: string, context?: LoggerContext): void {
    if (!this.shouldLog("info")) return;

    const entry = this.createLogEntry("info", message, context);
    this.logToConsole(entry);
  }

  warn(message: string, context?: LoggerContext): void {
    if (!this.shouldLog("warn")) return;

    const entry = this.createLogEntry("warn", message, context);
    this.logToConsole(entry);
  }

  error(message: string, context?: LoggerContext): void {
    if (!this.shouldLog("error")) return;

    const entry = this.createLogEntry("error", message, context);
    this.logToConsole(entry);
  }

  async time<T>(
    operation: string,
    fn: () => Promise<T>,
    context?: LoggerContext,
  ): Promise<T> {
    const tracker = this.config.performanceTracker;
    if (tracker) {
      return tracker.measureAsync(operation, fn, context);
    }

    // Fallback implementation
    const start = performance.now();
    const timerContext = {
      ...context,
      correlationId: this.correlationId || undefined,
    };

    try {
      this.debug(`Starting ${operation}`, timerContext);
      const result = await fn();
      const duration = performance.now() - start;

      this.info(`Completed ${operation}`, {
        ...timerContext,
        duration,
        success: true,
      });

      return result;
    } catch (error) {
      const duration = performance.now() - start;

      this.error(`${operation} failed`, {
        ...timerContext,
        duration,
        success: false,
        error,
      });

      throw error;
    }
  }

  timeSync<T>(operation: string, fn: () => T, context?: LoggerContext): T {
    const tracker = this.config.performanceTracker;
    if (tracker) {
      return tracker.measureSync(operation, fn, context);
    }

    // Fallback implementation
    const start = performance.now();
    const timerContext = {
      ...context,
      correlationId: this.correlationId || undefined,
    };

    try {
      this.debug(`Starting ${operation}`, timerContext);
      const result = fn();
      const duration = performance.now() - start;

      this.info(`Completed ${operation}`, {
        ...timerContext,
        duration,
        success: true,
      });

      return result;
    } catch (error) {
      const duration = performance.now() - start;

      this.error(`${operation} failed`, {
        ...timerContext,
        duration,
        success: false,
        error,
      });

      throw error;
    }
  }

  setCorrelationId(id: string | null): void {
    this.correlationId = id;
  }

  getCorrelationId(): string | null {
    return this.correlationId;
  }

  generateCorrelationId(): string {
    const id = this.config.correlationIdGenerator?.() || uuidv4();
    this.setCorrelationId(id);
    return id;
  }

  withContext(context: LoggerContext): Logger {
    const newLogger = new Logger(this.config);
    newLogger.correlationId = this.correlationId;
    newLogger.context = { ...this.context, ...context };
    return newLogger;
  }

  clearContext(): void {
    this.context = {};
  }

  getContext(): LoggerContext {
    return { ...this.context };
  }
}
